import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { api } from "../api";
import type { AuthConfig } from "../api";
import { LAYOUT_NAME } from "../nav";
import { useAuthActions } from "../session";
import { Avatar } from "../shell/Avatar";
import { Alert, Card, Field } from "../shell/ui";

export function useAuthConfig() {
  return useQuery({
    queryKey: ["auth-config"],
    queryFn: () => api<AuthConfig>("/auth/config"),
    staleTime: Infinity,
  });
}

/** Only same-site paths: `?next=` must never send anyone off to another origin. */
export function safeNext(next: string | null): string {
  return next && next.startsWith("/") && !next.startsWith("//") ? next : "/me";
}

export function SignInPage() {
  const config = useAuthConfig();
  const { demo, login } = useAuthActions();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const onLogin = (e: FormEvent) => {
    e.preventDefault();
    login.mutate({ username, password }, { onSuccess: () => navigate(next) });
  };

  return (
    <div className="auth">
      <h1 className="page__title">Sign in</h1>
      {(config.data?.personas.length ?? 0) > 0 && (
        <>
          <p className="muted">Try it with one click, or sign in with your own account below.</p>
          <div className="persona-grid">
            {config.data?.personas.map((p) => (
              <button
                key={p.key}
                type="button"
                className={`persona persona--${p.role}`}
                disabled={demo.isPending}
                onClick={() =>
                  demo.mutate(p.key, { onSuccess: () => navigate(p.key === "admin" ? "/admin" : next) })
                }
              >
                <Avatar seed={p.avatarSeed} size={44} />
                <span className="persona__body">
                  <span className="persona__name">
                    {p.displayName}
                    <span className={`level-pill level-pill--${p.role === "admin" ? "pro" : p.role}`}>
                      {LAYOUT_NAME[p.role]}
                    </span>
                  </span>
                  <span className="persona__tagline">{p.tagline}</span>
                </span>
              </button>
            ))}
          </div>
          {demo.error && <Alert tone="error">{demo.error.message}</Alert>}
        </>
      )}

      <Card title="Your account">
        <form className="auth__form" onSubmit={onLogin}>
          <Field label="Username">
            <input
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </Field>
          <Field label="Password">
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </Field>
          {login.error && <Alert tone="error">{login.error.message}</Alert>}
          <div className="row">
            <button className="btn btn--primary" type="submit" disabled={login.isPending}>
              Sign in
            </button>
            <Link to="/signup">No account yet? Create one — it's free.</Link>
          </div>
        </form>
      </Card>
    </div>
  );
}
